import { Injectable } from '@angular/core';
import { Observable, from } from 'rxjs';
import { map } from 'rxjs/operators';
import { environment } from '@eview/core';

@Injectable({
  providedIn: 'root'
})
export class GoogleGeocodingService {
  private apiKey = environment.googleMapsApiKey;

  forward(address: string): Observable<any[]> {
    return from(this.searchPlacesNew(address)).pipe(
      map(results => results)
    );
  }

  reverse(lat: number, lng: number): Observable<any[]> {
    return from(this.reverseGeocode(lat, lng)).pipe(
      map(results => results)
    );
  }

  private async searchPlacesNew(input: string): Promise<any[]> {
    try {
      const response = await fetch(
        `https://places.googleapis.com/v1/places:autocomplete`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Goog-Api-Key': this.apiKey
          },
          body: JSON.stringify({
            input: input,
            locationBias: {
              rectangle: {
                low: { latitude: -18.3479753557, longitude: -81.3867231649 },
                high: { latitude: -0.0572054988, longitude: -68.6650797187 }
              }
            },
            includedRegionCodes: ['PE']
          })
        }
      );

      const data = await response.json();
      
      if (!data.suggestions || data.suggestions.length === 0) {
        return [];
      }

      const detailPromises = data.suggestions.map(suggestion => 
        this.getPlaceDetailsNew(suggestion.placePrediction.placeId)
      );
      
      const results = await Promise.all(detailPromises);
      return results.filter(r => r !== null);
    } catch (error) {
      console.error('Error en búsqueda:', error);
      return [];
    }
  }

  private async getPlaceDetailsNew(placeId: string): Promise<any> {
    try {
      const response = await fetch(
        `https://places.googleapis.com/v1/places/${placeId}`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'X-Goog-Api-Key': this.apiKey,
            'X-Goog-FieldMask': 'id,displayName,formattedAddress,location,types,addressComponents'
          }
        }
      );

      const place = await response.json();
      const name = place.displayName?.text || '';
      const address = place.formattedAddress || '';
      const types = place.types || [];
      
      let displayText = address;
      if (name && name !== address) {
        displayText = name;
        if (types.includes('establishment') || types.includes('point_of_interest')) {
          displayText = `${name} - ${address}`;
        }
      }
      
      const addressComponents = place.addressComponents || [];
      const adminData = this.extractAdminLevels(addressComponents);
      
      return {
        display_name: displayText,
        name: name,
        address: address,
        lat: place.location?.latitude,
        lon: place.location?.longitude,
        place_id: place.id,
        ...adminData
      };
    } catch (error) {
      console.error('Error obteniendo detalles:', error);
      return null;
    }
  }

  private extractAdminLevels(addressComponents: any[]): any {
    const result = {
      mgmt_lev_1: '',
      mgmt_lev_2: '',
      mgmt_lev_3: ''
    };

    addressComponents.forEach(component => {
      const types = component.types || [];
      if (types.includes('administrative_area_level_1')) {
        result.mgmt_lev_1 = component.longText || component.shortText || '';
      } else if (types.includes('administrative_area_level_2')) {
        result.mgmt_lev_2 = component.longText || component.shortText || '';
      } else if (types.includes('locality') || types.includes('administrative_area_level_3')) {
        result.mgmt_lev_3 = component.longText || component.shortText || '';
      }
    });

    return result;
  }

  private async reverseGeocode(lat: number, lng: number): Promise<any[]> {
    try {
      const response = await fetch(
        `https://maps.googleapis.com/maps/api/geocode/json?latlng=${lat},${lng}&key=${this.apiKey}`
      );
      
      const data = await response.json();
      
      if (data.status === 'OK' && data.results) {
        return data.results.map(result => ({
          display_name: result.formatted_address,
          lat: result.geometry.location.lat,
          lon: result.geometry.location.lng,
          place_id: result.place_id
        }));
      }
      
      return [];
    } catch (error) {
      console.error('Error en geocodificación inversa:', error);
      return [];
    }
  }
}
